import type { DTechFileConfig } from '../types/DTechFileConfig';
import { DTechSubSecondCalculator } from './DTechSubSecondCalculator';

/**
 * Timestamp parsing utility for D-Tech files
 * Handles both legacy (DD/M/YYYY) and newer (YYYY/MM/DD) timestamp formats
 */
export class DTechTimestampParser {
  
  /**
   * Parse a D-Tech timestamp string based on the expected format
   * @param timeStr The timestamp string to parse
   * @param format The expected timestamp format from config
   * @returns Parsed Date object or null if invalid
   */
  static parseTimestamp(
    timeStr: string, 
    format: DTechFileConfig['timestampFormat']
  ): Date | null {
    if (!timeStr || !timeStr.trim()) {
      return null;
    }

    const cleanTimeStr = timeStr.trim();

    try {
      switch (format) {
        case 'DD/M/YYYY HH:mm:ss':
          return this.parseLegacyFormat(cleanTimeStr);
        
        case 'YYYY/MM/DD HH:mm:ss':
          return this.parseNewFormat(cleanTimeStr);
        
        default:
          // Try both formats if format is unknown
          return this.parseLegacyFormat(cleanTimeStr) || this.parseNewFormat(cleanTimeStr);
      }
    } catch (error) {
      return null;
    }
  }

  /**
   * Parse legacy D-Tech timestamp format: "16/4/2025 13:07:33"
   * Format: DD/M/YYYY HH:mm:ss (day and month can be 1 or 2 digits)
   */
  private static parseLegacyFormat(timeStr: string): Date | null {
    // Pattern: DD/M/YYYY HH:mm:ss or D/M/YYYY HH:mm:ss
    const match = timeStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/);
    
    if (!match) {
      return null;
    }

    const [, day, month, year, hour, minute, second] = match;
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);
    const hourNum = parseInt(hour, 10);
    const minuteNum = parseInt(minute, 10);
    const secondNum = parseInt(second, 10);
    
    // Create date object (month is 0-indexed in JavaScript)
    const date = new Date(yearNum, monthNum - 1, dayNum, hourNum, minuteNum, secondNum);

    return this.validateDate(date, yearNum, monthNum, dayNum, hourNum, minuteNum, secondNum) ? date : null;
  }

  /**
   * Parse newer D-Tech timestamp format: "2025/07/25 10:29:39" 
   * Format: YYYY/MM/DD HH:mm:ss
   */
  private static parseNewFormat(timeStr: string): Date | null {
    // Pattern: YYYY/MM/DD HH:mm:ss
    const match = timeStr.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2}):(\d{2})$/);
    
    if (!match) {
      return null;
    }

    const [, year, month, day, hour, minute, second] = match;
    const yearNum = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    const hourNum = parseInt(hour, 10);
    const minuteNum = parseInt(minute, 10);
    const secondNum = parseInt(second, 10);
    
    // Create date object (month is 0-indexed in JavaScript)
    const date = new Date(yearNum, monthNum - 1, dayNum, hourNum, minuteNum, secondNum);

    return this.validateDate(date, yearNum, monthNum, dayNum, hourNum, minuteNum, secondNum) ? date : null;
  }

  /**
   * Validate that a parsed date is reasonable for D-Tech data and matches original components
   * Rejects obviously invalid dates but allows basic validation to 
   * centralized DataQualityService for statistical outlier detection
   */
  private static validateDate(
    date: Date, 
    originalYear?: number, 
    originalMonth?: number, 
    originalDay?: number, 
    originalHour?: number, 
    originalMinute?: number, 
    originalSecond?: number
  ): boolean {
    // Basic validity check
    if (isNaN(date.getTime())) {
      return false;
    }

    // If original components provided, ensure they match the parsed date
    // This prevents JavaScript's auto-correction of invalid dates
    if (originalYear !== undefined) {
      if (date.getFullYear() !== originalYear) return false;
      if (originalMonth !== undefined && (date.getMonth() + 1) !== originalMonth) return false;
      if (originalDay !== undefined && date.getDate() !== originalDay) return false;
      if (originalHour !== undefined && date.getHours() !== originalHour) return false;
      if (originalMinute !== undefined && date.getMinutes() !== originalMinute) return false;
      if (originalSecond !== undefined && date.getSeconds() !== originalSecond) return false;
    }

    // Very basic range check (don't be too strict here - let DataQualityService handle outliers)
    const year = date.getFullYear();
    const minYear = 2000; // Earliest reasonable D-Tech data
    const maxYear = new Date().getFullYear() + 1; // Allow slight future dates
    
    if (year < minYear || year > maxYear) {
      return false;
    }

    // Check for obviously invalid month/day combinations
    const month = date.getMonth() + 1; // Convert back to 1-indexed for validation
    const day = date.getDate();
    
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return false;
    }

    // Basic hour validation
    const hour = date.getHours();
    if (hour < 0 || hour > 23) {
      return false;
    }

    return true;
  }

  /**
   * Auto-detect timestamp format from a sample string
   * @param timeStr Sample timestamp string
   * @returns Detected format or null if unable to determine
   */
  static detectFormat(timeStr: string): DTechFileConfig['timestampFormat'] | null {
    if (!timeStr || !timeStr.trim()) {
      return null;
    }

    const cleanTimeStr = timeStr.trim();

    // Try new format first (YYYY/MM/DD)
    if (/^\d{4}\/\d{1,2}\/\d{1,2}\s+\d{1,2}:\d{2}:\d{2}$/.test(cleanTimeStr)) {
      return 'YYYY/MM/DD HH:mm:ss';
    }

    // Try legacy format (DD/M/YYYY)
    if (/^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2}$/.test(cleanTimeStr)) {
      return 'DD/M/YYYY HH:mm:ss';
    }

    return null;
  }

  /**
   * Parse multiple timestamps from data array with format auto-detection and sub-second support
   * @param data Array of data rows
   * @param timeColumnName Name of the timestamp column
   * @param config DTech file configuration (includes timestamp format and sub-second settings)
   * @returns Object with parsed timestamps and detected format
   */
  static parseTimestamps(
    data: Record<string, string>[],
    timeColumnName: string,
    config: DTechFileConfig
  ): {
    timestamps: Date[];
    validIndices: number[];
    detectedFormat: DTechFileConfig['timestampFormat'] | null;
    parseErrors: string[];
  } {
    const timestamps: Date[] = [];
    const validIndices: number[] = [];
    const parseErrors: string[] = [];
    let detectedFormat: DTechFileConfig['timestampFormat'] | null = config.timestampFormat || null;

    // Auto-detect format from first valid timestamp if not provided in config
    if (!detectedFormat) {
      for (let i = 0; i < Math.min(data.length, 10); i++) {
        const timeStr = data[i][timeColumnName];
        if (timeStr) {
          const detected = this.detectFormat(timeStr);
          if (detected) {
            detectedFormat = detected;
            break;
          }
        }
      }
    }

    if (!detectedFormat) {
      parseErrors.push('Unable to detect timestamp format from data');
      return { timestamps, validIndices, detectedFormat: null, parseErrors };
    }

    // Check if sub-second sampling is enabled
    if (config.subSecondSampling) {
      return this.parseWithSubSecondSampling(data, timeColumnName, detectedFormat, config, parseErrors);
    }

    // Standard timestamp parsing (no sub-second processing)
    for (let i = 0; i < data.length; i++) {
      const timeStr = data[i][timeColumnName];
      
      if (!timeStr || !timeStr.trim()) {
        continue; // Skip empty timestamps
      }

      const parsed = this.parseTimestamp(timeStr, detectedFormat);
      
      if (parsed) {
        timestamps.push(parsed);
        validIndices.push(i);
      } else {
        // Log parsing errors but don't fail the entire operation
        parseErrors.push(`Invalid timestamp at row ${i + 1}: "${timeStr}"`);
      }
    }

    return {
      timestamps,
      validIndices,
      detectedFormat,
      parseErrors
    };
  }

  /**
   * Parse timestamps with sub-second sampling support
   * @param data Array of data rows
   * @param timeColumnName Name of the timestamp column
   * @param detectedFormat Detected timestamp format
   * @param config DTech file configuration
   * @param parseErrors Array to collect parsing errors
   * @returns Object with parsed timestamps and metadata
   */
  private static parseWithSubSecondSampling(
    data: Record<string, string>[],
    timeColumnName: string,
    detectedFormat: DTechFileConfig['timestampFormat'] | null,
    config: DTechFileConfig,
    parseErrors: string[]
  ): {
    timestamps: Date[];
    validIndices: number[];
    detectedFormat: DTechFileConfig['timestampFormat'] | null;
    parseErrors: string[];
  } {
    const timestamps: Date[] = [];
    const validIndices: number[] = [];

    // Ensure we have a valid format
    if (!detectedFormat) {
      parseErrors.push('No timestamp format detected for sub-second sampling');
      return { timestamps, validIndices, detectedFormat: null, parseErrors };
    }

    // Find the sample number column
    const sampleColumnName = DTechSubSecondCalculator.findSampleColumn(data[0] || {});
    if (!sampleColumnName) {
      parseErrors.push('Sample number column not found for sub-second sampling');
      console.warn('⚠️ Sub-second sampling enabled but no sample column found, falling back to standard parsing');
      
      // Fall back to standard parsing
      for (let i = 0; i < data.length; i++) {
        const timeStr = data[i][timeColumnName];
        if (!timeStr || !timeStr.trim()) continue;
        
        const parsed = this.parseTimestamp(timeStr, detectedFormat);
        if (parsed) {
          timestamps.push(parsed);
          validIndices.push(i);
        } else {
          parseErrors.push(`Invalid timestamp at row ${i + 1}: "${timeStr}"`);
        }
      }
      
      return { timestamps, validIndices, detectedFormat, parseErrors };
    }

    // Extract sample numbers
    const sampleResult = DTechSubSecondCalculator.extractSampleNumbers(data, sampleColumnName);
    parseErrors.push(...sampleResult.extractionErrors);

    console.log(`📊 Sub-second processing: Found ${sampleResult.sampleNumbers.length} valid samples out of ${data.length} rows`);

    // Process each row with valid sample numbers
    for (let i = 0; i < sampleResult.sampleNumbers.length; i++) {
      const dataIndex = sampleResult.validIndices[i];
      const sampleNumber = sampleResult.sampleNumbers[i];
      const timeStr = data[dataIndex][timeColumnName];

      if (!timeStr || !timeStr.trim()) {
        parseErrors.push(`Missing timestamp at row ${dataIndex + 1}`);
        continue;
      }

      // Parse base timestamp
      const baseTimestamp = this.parseTimestamp(timeStr, detectedFormat);
      if (!baseTimestamp) {
        parseErrors.push(`Invalid timestamp at row ${dataIndex + 1}: "${timeStr}"`);
        continue;
      }

      // Calculate sub-second timestamp
      const preciseTimestamp = DTechSubSecondCalculator.calculateSubSecondTimestamp(
        baseTimestamp,
        sampleNumber,
        config
      );

      if (preciseTimestamp) {
        timestamps.push(preciseTimestamp);
        validIndices.push(dataIndex);
      } else {
        parseErrors.push(`Failed to calculate sub-second timestamp at row ${dataIndex + 1} for sample ${sampleNumber}`);
      }
    }

    console.log(`⏰ Generated ${timestamps.length} sub-second timestamps using ${detectedFormat} format`);

    return {
      timestamps,
      validIndices,
      detectedFormat,
      parseErrors
    };
  }
}